// Funil Macro por DATA DO EVENTO — cada etapa conta pela data em que ela própria
// aconteceu (não pela data de criação do lead). Deliberadamente separado de
// src/lib/funnelUtils.ts (o funil de coorte usado por FunilDinamico.tsx e
// ProdutividadeDashboard.tsx, que continuam precisando do modelo "de quem entrou,
// quanto avançou" — não mexer lá).
//
// Consequência aceita: como cada etapa vem de um grupo de negociações diferente, o
// funil deixa de ser garantidamente decrescente (uma etapa pode ter mais itens que a
// anterior no mesmo período).
import { inPeriodo } from '../../../src/lib/periodoFiltro.ts';
import type { CustomRange, Periodo } from '../../../src/lib/periodoFiltro.ts';
import { aprovacaoAindaValida } from '../../../src/lib/fechamentoMensal.ts';
import type { AnaliseCadastralRow } from '../../../src/lib/fechamentoMensal.ts';

export interface EventFunnelRow extends AnaliseCadastralRow {
    status: string;
    created_at: string;
    data_assinatura_termo?: string | null;
    url_simulacao?: Array<{ data_criacao?: string | null }> | null;
    url_termo?: Array<{ data_criacao?: string | null }> | null;
    /** #6331: carimbado pelo banco ao entrar em 'termo_reprovado' (a última reprovação). */
    termo_reprovado_em?: string | null;
    termo_reprovado_motivo?: string | null;
    /** true = data antiga reconstruída (backfill), não o momento exato. */
    termo_reprovado_aproximado?: boolean | null;
}

const earliest = (dates: Array<string | null | undefined>): string | null => {
    const validas = dates.filter((d): d is string => !!d).sort();
    return validas[0] || null;
};

export interface FunnelStageDef {
    key: string;
    label: string;
    color: string;
    dataDe: (r: EventFunnelRow) => string | null;
    /**
     * 'saida' = desfecho que tira a negociação do funil (Termo Reprovado), não a
     * etapa seguinte. Não entra nas taxas etapa-a-etapa nem na sequência linear;
     * `anterior` diz de qual etapa ela sai.
     */
    tipo?: 'saida';
    anterior?: string;
}

export const FUNNEL_STAGE_DEFS: FunnelStageDef[] = [
    { key: 'prospeccao', label: 'Prospecção', color: '#3b82f6', dataDe: r => r.created_at || null },
    { key: 'simulacao', label: 'Simulação', color: '#a855f7', dataDe: r => earliest((r.url_simulacao || []).map(s => s.data_criacao)) },
    { key: 'termo_enviado', label: 'Termo Enviado', color: '#eab308', dataDe: r => earliest((r.url_termo || []).map(t => t.data_criacao)) },
    { key: 'termo_assinado', label: 'Termo Assinado', color: '#14b8a6', dataDe: r => r.data_assinatura_termo || null },
    { key: 'termo_aprovado', label: 'Termo Aprovado', color: '#22c55e', dataDe: r => aprovacaoAindaValida(r) },
];

/**
 * Saídas do funil (#6331). Ficam FORA de FUNNEL_STAGE_DEFS de propósito: as taxas
 * etapa-a-etapa do Analytics, a Visão Geral e o ranking percorrem aquela lista em
 * sequência, e "Aprovado → Reprovado" não é uma conversão. Só quem pede
 * `incluirSaidas` as recebe.
 *
 * A data é a do evento (`termo_reprovado_em`), não o status atual: a reprovada que
 * depois foi recuperada continua contando no mês em que foi reprovada.
 */
export const FUNNEL_SAIDA_DEFS: FunnelStageDef[] = [
    { key: 'termo_reprovado', label: 'Termo Reprovado', color: '#ef4444', dataDe: r => r.termo_reprovado_em || null, tipo: 'saida', anterior: 'termo_assinado' },
];

/** Etapa ou saída pela chave. */
export const defDaEtapa = (key: string): FunnelStageDef | undefined =>
    FUNNEL_STAGE_DEFS.find(d => d.key === key) ?? FUNNEL_SAIDA_DEFS.find(d => d.key === key);

export interface EventStage {
    key: string;
    label: string;
    color: string;
    rows: EventFunnelRow[];
    leads: number;
    kwh: number;
    tipo?: 'saida';
}

/** Pra cada etapa, filtra `rows` pelas que têm a data-do-evento dentro do período
 * selecionado — independente do status atual ou de quando a negociação foi criada. */
export function computeEventFunnel(
    rows: EventFunnelRow[],
    periodo: Periodo,
    customRange: CustomRange,
    opcoes: { incluirSaidas?: boolean } = {},
): EventStage[] {
    const defs = opcoes.incluirSaidas ? [...FUNNEL_STAGE_DEFS, ...FUNNEL_SAIDA_DEFS] : FUNNEL_STAGE_DEFS;
    return defs.map(def => {
        const matching = rows.filter(r => {
            const data = def.dataDe(r);
            return !!data && inPeriodo(data, periodo, customRange);
        });
        return {
            key: def.key,
            label: def.label,
            color: def.color,
            rows: matching,
            leads: matching.length,
            kwh: matching.reduce((s, r) => s + (r.consumo_medio_kwh || 0), 0),
            ...(def.tipo ? { tipo: def.tipo } : {}),
        };
    });
}

/**
 * Taxa de reprovação do período (#6331): reprovados ÷ (aprovados + reprovados).
 * Sobre as DECISÕES do período, não sobre a prospecção — senão o número fica
 * minúsculo e não diz nada. `null` quando não houve decisão nenhuma.
 */
export function taxaReprovacao(stages: readonly Pick<EventStage, 'key' | 'leads'>[]): number | null {
    const aprovados = stages.find(s => s.key === 'termo_aprovado')?.leads ?? 0;
    const reprovados = stages.find(s => s.key === 'termo_reprovado')?.leads ?? 0;
    const decisoes = aprovados + reprovados;
    return decisoes > 0 ? (reprovados / decisoes) * 100 : null;
}
